import React, { useEffect, useState, useMemo } from "react";
import { Box, TextField, InputAdornment, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LaunchIcon from "@mui/icons-material/Launch";
import { obtenerDatosSites } from "../../api/config";
import { GlobalContext } from "../../GlobalContext";
import { useNavigate } from "react-router-dom";

import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";

const CustomToolbar = ({ searchTerm, onChange, label }) => (
	<GridToolbarContainer
		sx={{
			px: 1,
			py: 1,
			borderBottom: 1,
			borderColor: "divider",
			width: "100%",
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<Box sx={{ width: "24%", minWidth: 260 }}>
			<TextField
				sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
				fullWidth
				size="small"
				label={label}
				value={searchTerm}
				onChange={onChange}
				type="search"
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<SearchIcon />
						</InputAdornment>
					),
				}}
			/>
		</Box>
		<Box sx={{ flex: 1 }} />
	</GridToolbarContainer>
);

const twoDigits = (n) => {
	const num = Number(n);
	return Number.isFinite(num) ? String(Math.abs(num)).padStart(2, "0") : "--";
};
const withSign2 = (n) => {
	const num = Number(n);
	if (!Number.isFinite(num)) return "--";
	return `${num < 0 ? "-" : ""}${twoDigits(num)}`;
};
const covColor = (v) =>
	Number(v) > 0 ? "success.main" : Number(v) === 0 ? "text.secondary" : "error.main";
const holgColor = (v) =>
	Number(v) < 0 ? "success.main" : Number(v) === 0 ? "text.secondary" : "error.main";

const HomeOfHomes = () => {
	const navigate = useNavigate();
	const [searchTermAvailable, setSearchTermAvailable] = useState("");
	const [datos, setDatos] = useState([]);
	const [loading, setLoading] = useState(true);

	const { setSiteId } = React.useContext(GlobalContext);
	const { t } = useTranslation();
	const [paginationModel, setPaginationModel] = React.useState({
		pageSize: 10,
		page: 0,
	});
	useEffect(() => {
		const obtenerDatos = async () => {
			try {
				setLoading(true); // Inicia la carga
				const data = await obtenerDatosSites("/site/get_fully_names");
				setDatos(data);
				setLoading(false);
			} catch (error) {
				console.error("Error en obtenerDatosSites:", error);
			}
		};
		obtenerDatos();
	}, []);

	const filterDatos = (datosIn, searchTerm) => {
		if (Array.isArray(datosIn)) {
			// Si ya viene array, filtramos array
			if (!searchTerm) return datosIn;
			return datosIn.filter((item) =>
				String(item?.full_name || "")
					.toLowerCase()
					.includes(searchTerm)
			);
		}

		if (searchTerm) {
			return (
				Object.keys(datosIn || {})
					.filter(
						(key) =>
							key.toLowerCase().includes(searchTerm) ||
							(datosIn[key].full_name && datosIn[key].full_name.toLowerCase().includes(searchTerm))
					)
					// eslint-disable-next-line no-sequences
					.reduce((res, key) => ((res[key] = datosIn[key]), res), {})
			);
		} else {
			return datosIn;
		}
	};

	const filteredDatos = useMemo(
		() => filterDatos(datos, searchTermAvailable.toLowerCase()),
		[datos, searchTermAvailable]
	);

	const handleSearchChange = (e) => {
		setSearchTermAvailable(e.target.value);
	};

	const rows = useMemo(() => {
		if (Array.isArray(filteredDatos)) return filteredDatos;
		return Object.keys(filteredDatos || {}).map((k) => filteredDatos[k]);
	}, [filteredDatos]);

	console.log("rows:", rows);

	const handleSelect = (data) => {
		const id = data.id;
		setSiteId(id);
		localStorage.setItem("siteId", id);
		localStorage.setItem("siteName", data.full_name);
		navigate("/home");
	};

	const columns = useMemo(
		() => [
			{
				field: "full_name",
				headerName: t("labels.storeOrRegion") || "Tienda o región",
				flex: 2.5,
				minWidth: 340,
				sortable: true,
				resizable: false,
				renderCell: (params) => (
					<Box
						sx={{
							whiteSpace: "normal",
							overflowWrap: "anywhere",
							wordBreak: "break-word",
							width: "100%",
							lineHeight: 1.25,
							pr: 1,
						}}
					>
						{params.value}
					</Box>
				),
			},
			{
				field: "var_cobertura",
				headerName: t("labels.realCoverageVar") || "Variación cobertura real",
				type: "number",
				align: "center",
				headerAlign: "center",
				flex: 1,
				minWidth: 100,
				resizable: false,
				renderCell: (p) => (
					<Box sx={{ fontWeight: 700, color: covColor(p.value) }}>{withSign2(p.value)}</Box>
				),
				sortComparator: (a, b) => Number(a) - Number(b),
			},
			{
				field: "var_holgura",
				headerName: t("labels.slackVar") || "Variación holgura",
				type: "number",
				align: "center",
				headerAlign: "center",
				flex: 1,
				minWidth: 100,
				resizable: false,
				renderCell: (p) => (
					<Box sx={{ fontWeight: 700, color: holgColor(p.value) }}>{withSign2(p.value)}</Box>
				),
				sortComparator: (a, b) => Number(a) - Number(b),
			},
			{
				field: "__acciones",
				headerName: "",
				sortable: false,
				filterable: false,
				align: "right",
				headerAlign: "right",
				flex: 1.5,
				width: 160,
				resizable: false,
				renderCell: (p) => (
					<Button
						size="small"
						endIcon={<LaunchIcon />}
						onClick={() => handleSelect(p.row)} // <== MISMA ACCIÓN QUE LA LISTA
					>
						{t("aux.viewDetail") || "Ver detalle"}
					</Button>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	console.log("columns:", columns);

	return (
		<Box
			sx={{
				width: "95%",
				height: "80vh",
				border: 1,
				borderColor: "secondary.main",

				justifyContent: "space-between",
				borderRadius: "20px",
				overflow: "hidden",
			}}
		>
			{loading ? (
				<Box
					sx={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<CircularProgress color="primary" />
				</Box>
			) : (
				<>
					<Box sx={{ width: "100%" }} />
					<DataGrid
						rows={rows}
						getRowId={(row) => row.id ?? `${row.full_name}-${row.period ?? ""}`}
						columns={columns}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						pagination={paginationModel.pageSize !== -1}
						pageSizeOptions={[10, 25, 50, { label: "Ver todos", value: -1 }]}
						disableColumnResize
						rowHeight={52}
						slots={{ toolbar: CustomToolbar }}
						slotProps={{
							toolbar: {
								searchTerm: searchTermAvailable,
								onChange: handleSearchChange,
								label: t("aux.search"),
							},
						}}
						sx={{
							border: 0,
							"& .MuiDataGrid-columnHeaders": { bgcolor: "#faf7f5" },
							"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
						}}
					/>
				</>
			)}
		</Box>
	);
};

export { HomeOfHomes };
